<template>
  <v-dialog
    v-model="show"
    max-width="1200"
    scrollable
    content-class="ds-dialog-alto"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    transition="dialog-bottom-transition"
  >
    <v-card class="rl-card">
      <v-card-title class="rl-header">
        <v-icon color="white" small class="mr-2">mdi-message-text-outline</v-icon>
        <span>Recados</span>
        <v-spacer />
        <v-btn small text color="white" class="rl-new" @click="$emit('open-compose')">
          <v-icon left small>mdi-plus</v-icon>
          Novo recado
        </v-btn>
        <v-btn class="close-btn" icon color="white" @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Filtros fixos (não rolam com a lista), num cartão sobre o fundo areia. -->
      <div class="rl-filters-wrap">
        <section class="ds-form-section rl-filters">
          <v-select
            v-model="filterParty"
            :items="partyOptions"
            item-text="name"
            item-value="id"
            label="Interlocutor"
            dense
            outlined
            hide-details="auto"
            clearable
            class="rl-filter"
          />
          <v-select
            v-model="filterYear"
            :items="yearOptions"
            label="Ano"
            dense
            outlined
            hide-details="auto"
            clearable
            class="rl-filter rl-filter--sm"
          />
          <v-select
            v-model="filterMonth"
            :items="monthOptions"
            item-text="text"
            item-value="value"
            label="Mês"
            dense
            outlined
            hide-details="auto"
            clearable
            class="rl-filter rl-filter--sm"
          />
        </section>
      </div>

      <v-card-text class="rl-body">
        <div v-if="!filteredNotes.length" class="rl-empty">
          Nenhum recado com esses filtros.
        </div>

        <v-card
          v-for="n in filteredNotes"
          :key="n.id"
          outlined
          class="rl-note"
          :class="{ 'rl-note--pending': !n.resolved_at }"
          @click="$emit('open-thread', n)"
        >
          <div class="rl-note-head">
            <span class="rl-note-parties">
              {{ n.sender_name }} → {{ n.recipient_name || n.clinic_name }}
            </span>
            <span class="rl-note-date">{{ formatDate(n.created_at) }}</span>
          </div>
          <div class="rl-note-msg">{{ n.message }}</div>
          <div class="rl-note-foot">
            <v-chip
              x-small
              :color="n.resolved_at ? 'green' : 'amber darken-2'"
              text-color="white"
            >
              {{ n.resolved_at ? "Resolvido" : "Pendente" }}
            </v-chip>
            <span v-if="n.replies && n.replies.length" class="rl-note-replies">
              <v-icon x-small>mdi-reply</v-icon> {{ n.replies.length }}
            </span>
            <v-spacer />
            <v-btn
              v-if="!n.resolved_at"
              x-small
              text
              color="green darken-1"
              :loading="actingId === n.id"
              @click.stop="markRead(n)"
            >
              <v-icon left x-small>mdi-check</v-icon>
              Marcar como lida
            </v-btn>
          </div>
        </v-card>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { resolveAssistantNote } from "@/services/assistantNoteService";
import { formatDate } from "@/utils/datetime";

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export default {
  name: "RecadoListDialog",

  props: {
    value: { type: Boolean, default: false },
    notes: { type: Array, default: () => [] },
    currentUserId: { type: [Number, String], default: null },
  },

  data() {
    return { filterParty: null, filterYear: null, filterMonth: null, actingId: null };
  },

  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    partyOptions() {
      const map = new Map();
      for (const n of this.notes) {
        const mine = Number(n.sender_id) === Number(this.currentUserId);
        const otherId = mine ? n.recipient_id : n.sender_id;
        const otherName = mine ? n.recipient_name || n.clinic_name : n.sender_name;
        if (otherId && !map.has(otherId)) {
          map.set(otherId, { id: otherId, name: otherName || "—" });
        }
      }
      return [...map.values()];
    },
    yearOptions() {
      const s = new Set();
      for (const n of this.notes) {
        const d = new Date(n.created_at);
        if (!Number.isNaN(d.getTime())) s.add(d.getFullYear());
      }
      return [...s].sort((a, b) => b - a);
    },
    monthOptions() {
      return MONTHS.map((m, i) => ({ text: m, value: i + 1 }));
    },
    filteredNotes() {
      return this.notes.filter((n) => {
        if (this.filterParty) {
          const mine = Number(n.sender_id) === Number(this.currentUserId);
          const otherId = mine ? n.recipient_id : n.sender_id;
          if (Number(otherId) !== Number(this.filterParty)) return false;
        }
        const d = new Date(n.created_at);
        const ok = !Number.isNaN(d.getTime());
        if (this.filterYear && (!ok || d.getFullYear() !== this.filterYear)) return false;
        if (this.filterMonth && (!ok || d.getMonth() + 1 !== this.filterMonth)) return false;
        return true;
      });
    },
  },

  methods: {
    formatDate,
    async markRead(n) {
      this.actingId = n.id;
      try {
        await resolveAssistantNote(n.id);
        this.$emit("refresh");
      } catch (e) {
        console.error("Erro ao marcar recado como lido:", e);
      } finally {
        this.actingId = null;
      }
    },
  },
};
</script>

<style scoped>
.rl-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  padding: 12px 16px;
}
.rl-new {
  text-transform: none;
  letter-spacing: 0;
}
.rl-filters-wrap {
  flex: 0 0 auto;
  padding: 12px 16px 0;
  background-color: var(--ds-blue-bg-soft);
}
.rl-filters {
  display: flex;
  gap: 8px;
}
.rl-filter {
  flex: 1;
}
.rl-filter--sm {
  max-width: 110px;
}
.rl-body {
  padding: 12px 16px;
}
.rl-empty {
  font-size: 13px;
  color: var(--ds-text-soft, var(--ds-text-muted));
  text-align: center;
  padding: 24px 0;
}
.rl-note {
  border-radius: 10px !important;
  padding: 10px 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}
.rl-note:hover {
  box-shadow: 0 2px 10px rgba(31, 77, 63, 0.12) !important;
}
.rl-note--pending {
  border-left: 3px solid #f59e0b !important;
}
.rl-note-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: var(--ds-text-muted, var(--ds-text-muted));
  margin-bottom: 5px;
}
.rl-note-parties {
  font-weight: 700;
  color: var(--ds-primary, var(--ds-primary));
}
.rl-note-msg {
  font-size: 13px;
  line-height: 1.4;
  color: var(--ds-text-strong, #1c1917);
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.rl-note-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}
.rl-note-replies {
  font-size: 11px;
  color: var(--ds-text-muted, var(--ds-text-muted));
}
</style>
